import { useCallback, useState } from 'react'
import type { Creative, CreativeType, FacebookPostContent } from '../types'
import { useFlushPersistNow, useUpdateCreativeContent } from '../store/draft-creative-context'
import { FieldGroup } from '../components/FieldGroup'
import { ImagePickerModal } from '../components/ImagePickerModal'
import { useEditorHighlight } from '../hooks/useEditorHighlight'
import { ImagePickerButton } from './fields/ImagePickerButton'
import { VideoPickerBody } from './fields/VideoPickerBody'

interface FacebookPostEditorProps {
  creative: Creative<CreativeType.FacebookPost>
}

export function FacebookPostEditor({ creative }: FacebookPostEditorProps) {
  const updateContent = useUpdateCreativeContent()
  const flushPersistNow = useFlushPersistNow()
  const { editorRef, highlightedSection, handleEditorBlur, setActiveSection } = useEditorHighlight(
    creative.id
  )
  const content = creative.content

  const [imagePickerOpen, setImagePickerOpen] = useState(false)
  const hasVideo = !!(content.videoAttachmentId || content.videoUrl || content.videoStatus)

  const update = useCallback(
    (field: keyof FacebookPostContent, value: unknown) => {
      updateContent(creative.id, { [field]: value })
    },
    [creative.id, updateContent]
  )

  return (
    <div ref={editorRef} className="space-y-4" onBlurCapture={handleEditorBlur}>
      {/* Image */}
      <FieldGroup label="Image" sectionId="image" highlighted={highlightedSection === 'image'}>
        <ImagePickerButton imageUrl={content.imageUrl} onClick={() => setImagePickerOpen(true)} />
      </FieldGroup>

      {/* Video (Reel) — shown whenever the creative carries any video data,
          whether AI-generated (videoStatus set), user-uploaded (only
          videoAttachmentId set), or legacy (just videoUrl). */}
      {hasVideo && (
        <FieldGroup label="Video" sectionId="video" highlighted={highlightedSection === 'video'}>
          <VideoPickerBody
            creativeId={creative.id}
            creativeType={creative.type}
            platform="facebook"
            selectedVideoAttachmentId={content.videoAttachmentId}
            selectedVideoUrl={content.videoUrl}
            onSelectVideo={(attachmentId, videoUrl, videoSource) => {
              updateContent(creative.id, { videoAttachmentId: attachmentId, videoUrl, videoSource })
              flushPersistNow(creative.id)
            }}
          />
        </FieldGroup>
      )}

      {/* Message */}
      <FieldGroup
        label="Message"
        sectionId="message"
        highlighted={highlightedSection === 'message'}
      >
        <textarea
          value={content.message}
          onChange={e => update('message', e.target.value)}
          onFocusCapture={() => setActiveSection(creative.id, 'message')}
          placeholder="Write your message..."
          rows={4}
          className="w-full resize-none rounded-lg border border-line-bright bg-bg-3 px-3 py-2 text-sm text-paper transition-colors outline-none placeholder:text-paper-faint focus:border-acid"
        />
      </FieldGroup>

      {/* Image Picker Modal */}
      <ImagePickerModal
        creativeId={creative.id}
        currentImageUrl={content.imageUrl}
        currentAttachmentId={content.imageAttachmentId}
        open={imagePickerOpen}
        onOpenChange={setImagePickerOpen}
        onSelect={({ imageUrl, imageAttachmentId }) => {
          updateContent(creative.id, { imageUrl, imageAttachmentId })
          // Image swap is a discrete intentional action — fire the PUT now
          // instead of waiting for the 500ms debounce. Saves the edit if the
          // user refreshes right after picking.
          flushPersistNow(creative.id)
          setImagePickerOpen(false)
        }}
      />
    </div>
  )
}
